import React, { useState, useEffect } from "react";
import "../css/projectDisplay.css";
import { 
  FaFolder, 
  FaFolderOpen, 
  FaFile, 
  FaFilePdf, 
  FaFileWord, 
  FaFileExcel, 
  FaFileImage,
  FaFileArchive,
  FaDownload,
  FaClock,
  FaInfoCircle
} from "react-icons/fa";

const ProjectDisplay = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expandedFolders, setExpandedFolders] = useState({});

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    setLoading(true);
    try {
      const response = await fetch("https://auxititpt.org/Backend/project_api.php");
      if (!response.ok) throw new Error("Failed to fetch projects");
      const data = await response.json();
      setProjects(data);
    } catch (err) {
      setError("Error loading projects: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleFolder = (path) => {
    setExpandedFolders(prev => ({
      ...prev,
      [path]: !prev[path]
    }));
  };

  const renderFileTree = (items, path = "") => {
    if (!items || !Array.isArray(items)) return null;
    
    return items.map((item) => {
      const itemPath = `${path}/${item.name}`.replace("//", "/");
      const isExpanded = expandedFolders[itemPath];
      
      if (item.type === "folder") {
        return (
          <div key={itemPath} className="folder-item">
            <div 
              className="folder-header" 
              onClick={() => toggleFolder(itemPath)}
              style={{ cursor: 'pointer' }}
            >
              {isExpanded ? (
                <FaFolderOpen className="folder-icon text-warning me-1" />
              ) : (
                <FaFolder className="folder-icon text-warning me-1" />
              )}
              <span className="folder-name">{item.name}</span>
            </div>
            
            {isExpanded && item.children && item.children.length > 0 && (
              <div className="folder-children ms-3">
                {renderFileTree(item.children, item.path)}
              </div>
            )}
            
            {isExpanded && item.children && item.children.length === 0 && (
              <div className="empty-folder text-muted ms-3">
                This folder is empty
              </div>
            )}
          </div>
        );
      }
      
      return (
        <div key={itemPath} className="file-item d-flex align-items-center py-1">
          <div className="file-icon me-2">
            {getFileIcon(item.extension)}
          </div>
          <div className="file-info flex-grow-1">
            <div className="file-name">{item.name}</div>
            <div className="file-meta text-muted small">
              {item.size ? formatFileSize(item.size) : ''} • 
              {item.modified ? formatDate(item.modified) : ''}
            </div>
          </div>
          <div className="file-actions">
            <a 
              href={`https://auxititpt.org${item.path}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-sm btn-primary"
              title="Download file"
            >
              <FaDownload size={12} />
            </a>
          </div>
        </div>
      );
    });
  };

  const getFileIcon = (extension) => {
    const ext = extension?.toLowerCase() || '';
    switch (ext) {
      case 'pdf': return <FaFilePdf size={20} className="text-danger" />;
      case 'doc': 
      case 'docx': return <FaFileWord size={20} className="text-primary" />;
      case 'xls':
      case 'xlsx': return <FaFileExcel size={20} className="text-success" />;
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'bmp': return <FaFileImage size={20} className="text-warning" />;
      case 'zip':
      case 'rar':
      case '7z':
      case 'tar':
      case 'gz': return <FaFileArchive size={20} className="text-info" />;
      default: return <FaFile size={20} className="text-secondary" />;
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "";
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 Bytes';
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${parseFloat((bytes / Math.pow(1024, i)).toFixed(2))} ${sizes[i]}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <section className="explorer-container">
        <div className="header">
          <h2><FaFolder className="me-2" /> Project File Explorer</h2>
          <p className="text-muted">Browse all available projects and their files</p>
        </div>
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="explorer-container">
        <div className="header">
          <h2><FaFolder className="me-2" /> Project File Explorer</h2>
          <p className="text-muted">Browse all available projects and their files</p>
        </div>
        <div className="alert alert-danger m-3">{error}</div>
      </section>
    );
  }

  return (
    <section className="explorer-container">
      <div className="header">
        <h2><FaFolder className="me-2" /> Project File Explorer</h2>
        <p className="text-muted">Browse all available projects and their files</p>
      </div>

      <div className="projects-grid">
        {projects.map((project) => (
          <div key={project.id} className="project-card">
            <div className="project-header">
              <div className="project-icon">
                <FaFolderOpen size={28} className="text-warning" />
              </div>
              <div className="project-info">
                <h3>{project.project_name}</h3>
                <p className="text-muted mb-0">
                  <FaClock className="me-1" /> 
                  {formatDate(project.uploaded_at)}
                </p>
                {project.description && (
                  <p className="project-description mt-1 mb-0">
                    <FaInfoCircle className="me-1" />
                    {project.description.length > 100 
                      ? project.description.substring(0, 100) + '...' 
                      : project.description}
                  </p>
                )}
              </div>
            </div>
            
            <div className="file-explorer">
              {project.structure && project.structure.length > 0 ? (
                <div className="tree-view p-3">
                  {renderFileTree(project.structure, `/public/uploads/projects/${encodeURIComponent(project.project_name)}`)}
                </div>
              ) : (
                <div className="empty-state p-4">
                  <div className="empty-icon mb-2">
                    <FaFolder size={40} className="text-secondary" />
                  </div>
                  <p className="text-muted">This project has no files to display</p>
                </div>
              )}
            </div>
          </div>
        ))}
        
        {projects.length === 0 && (
          <div className="no-projects text-center py-5">
            <div className="empty-icon mb-3">
              <FaFolder size={56} className="text-muted" />
            </div>
            <h3>No Projects Available</h3>
            <p className="text-muted mb-4">There are currently no projects to display</p>
          </div>
        )}
      </div>
    </section>
  );
};

export default ProjectDisplay;